import {
    getAllcountryManagement,
    listAdministrativeLocations,
} from '../../countryManagement/countryManagement.service';

/**
 * Country and state options for a rainfall baseline, read from the country geo module.
 *
 * `rainfall_baselines.state` is matched by exact name against the level-1 administrative location
 * on the farmer's plot — GeoQueryService maps level 1 to `stateName`, level 2 to district, level 3
 * to block. A name that does not match one of those rows means C1_RAINFALL_DEFICIT silently never
 * fires, so the value is picked from the same rows the farmer's address was built from, and the
 * row's id is kept alongside the name.
 *
 * Deliberately NOT using countryManagement.service's getAllCountriesFlat: it pages at size=100 and
 * turns any failure into an empty array, so a rejected page size is indistinguishable from "no
 * countries exist" — which is exactly how this screen came to claim none were configured. Here a
 * failure is reported as a failure, and the page size matches what the Countries screen itself uses.
 *
 * Kept out of climate.service.js on purpose: that file is pure local state and its tests run
 * without the API layer.
 */

const payloadOf = (result) => result?.data?.data ?? result?.data ?? null;

/** Level 1 is what GeoQueryService reports as `stateName`, whatever a given country calls it. */
export const STATE_LEVEL = 1;

/** Countries carry status ACTIVE or DELETED; a blank status is treated as active server-side. */
const DELETED_STATUS = 'DELETED';

const PAGE_SIZE = 50;
const MAX_PAGES = 40;

/** What this country calls its level-1 unit — "State" in India, "Region" in Ghana. */
export function stateLevelLabel(country) {
    const types = country?.administrativeLocationTypes || [];
    return types.find((type) => type.level === STATE_LEVEL)?.name || 'State';
}

export function isActiveCountry(country) {
    const status = (country?.status || '').toString().trim().toUpperCase();
    return status !== DELETED_STATUS;
}

/**
 * Every country still listed on the Countries screen, newest page walked through to the end.
 *
 * Returns `failed` separately from an empty list: "the call did not work" and "nothing is
 * configured" need different messages, and conflating them is what produced a wrong one.
 */
export async function getCountries() {
    const collected = [];
    let page = 0;
    let more = true;

    while (more && page < MAX_PAGES) {
        let res;
        try {
            res = await getAllcountryManagement(page, PAGE_SIZE);
        } catch {
            return { countries: [], failed: true };
        }

        const content = res?.data?.content;
        if (!Array.isArray(content)) {
            // getAllcountryManagement resolves with the error object rather than throwing, so a
            // missing content array on the first page is the only signal a request failed.
            if (page === 0) return { countries: [], failed: true };
            break;
        }

        collected.push(...content);

        const totalPages = Number(res?.data?.totalPages || 0);
        more = totalPages > 0 ? page + 1 < totalPages : content.length === PAGE_SIZE;
        page += 1;
    }

    const countries = collected
        .filter((country) => country?.id)
        .filter(isActiveCountry)
        .map((country) => ({
            id: country.id,
            name: (country.name || country.countryName || '').toString().trim() || 'Unnamed country',
            status: country.status,
            administrativeLocationTypes: country.administrativeLocationTypes || [],
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

    return { countries, failed: false };
}

/**
 * The country's level-1 administrative locations, each with the id it has in the database.
 *
 * Inactive ones are included and flagged: a farmer recorded against a since-deactivated location
 * still resolves to its name, so a baseline for it is not necessarily dead config — but it should
 * not look like a normal choice either.
 */
export async function getStateOptions(countryId) {
    if (!countryId) return { options: [], failed: false };

    const result = await listAdministrativeLocations(countryId, null, '');
    if (!result?.success) return { options: [], failed: true };

    const rows = payloadOf(result);
    if (!Array.isArray(rows)) return { options: [], failed: true };

    const options = rows
        .filter((row) => row?.id && row?.name)
        .map((row) => ({
            id: row.id,
            name: row.name,
            code: row.code || null,
            active: row.active !== false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

    return { options, failed: false };
}
